import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { cva } from 'class-variance-authority';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type SVGProps,
} from 'react';
import { cn } from '../../../lib/utils';

const Collapsible = forwardRef<
  ElementRef<typeof CollapsiblePrimitive.Root>,
  ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root>
>(({ className, ...props }, ref) => (
  <CollapsiblePrimitive.Root
    ref={ref}
    data-slot="collapsible"
    className={cn('flex w-full min-w-0 flex-col gap-2', className)}
    {...props}
  />
));
Collapsible.displayName = CollapsiblePrimitive.Root.displayName ?? 'Collapsible';

const CollapsibleTrigger = forwardRef<
  ElementRef<typeof CollapsiblePrimitive.CollapsibleTrigger>,
  ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleTrigger>
>(({ className, children, ...props }, ref) => (
  <CollapsiblePrimitive.CollapsibleTrigger
    ref={ref}
    data-slot="collapsible-trigger"
    className={cn(
      'flex w-full min-w-0 items-center gap-4 rounded-md px-4 py-2 text-left outline-none transition-colors',
      'hover:bg-muted/50',
      'focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      'disabled:pointer-events-none disabled:opacity-50',
      className
    )}
    {...props}
  >
    <span className="min-w-0 flex-1 text-sm font-semibold leading-5 text-foreground">
      {children}
    </span>
    <span
      className="flex size-9 shrink-0 items-center justify-center rounded-md p-2 text-foreground"
      aria-hidden
    >
      <ChevronsUpDownIcon className="size-4 shrink-0" />
    </span>
  </CollapsiblePrimitive.CollapsibleTrigger>
));
CollapsibleTrigger.displayName =
  CollapsiblePrimitive.CollapsibleTrigger.displayName ?? 'CollapsibleTrigger';

const CollapsibleContent = forwardRef<
  ElementRef<typeof CollapsiblePrimitive.CollapsibleContent>,
  ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>
>(({ className, children, ...props }, ref) => (
  <CollapsiblePrimitive.CollapsibleContent
    ref={ref}
    data-slot="collapsible-content"
    className={cn(
      'overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',
      className
    )}
    {...props}
  >
    <div className="flex flex-col gap-2">{children}</div>
  </CollapsiblePrimitive.CollapsibleContent>
));
CollapsibleContent.displayName =
  CollapsiblePrimitive.CollapsibleContent.displayName ?? 'CollapsibleContent';

const collapsibleInsetVariants = cva(
  'rounded-lg border border-input bg-background px-4 py-3 text-sm leading-5 text-foreground'
);

export interface CollapsibleInsetProps extends HTMLAttributes<HTMLDivElement> {}

const CollapsibleInset = forwardRef<HTMLDivElement, CollapsibleInsetProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="collapsible-inset"
      className={cn(collapsibleInsetVariants(), className)}
      {...props}
    />
  )
);
CollapsibleInset.displayName = 'CollapsibleInset';

function ChevronsUpDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  );
}

export {
  ChevronsUpDownIcon,
  Collapsible,
  CollapsibleContent,
  CollapsibleInset,
  CollapsibleTrigger,
  collapsibleInsetVariants,
};
